import { zoneOverlayPolygon } from '../domain/liveView/zoneOverlayGeometry';
import type { EffectiveMeasurementDefinition } from '../services/effectiveMeasurementDefinitionApi';
import type { OverviewAreaGeometry } from './OverviewAreaCameraPanel';
import { overviewV2PublicZoneId } from './overviewV2Presentation';

function text(value: unknown): string {
  return String(value || '').trim();
}

function isProvenDeployedGeometry(geometry: { deployed?: boolean } | null | undefined): boolean {
  return Boolean(geometry && geometry.deployed === true);
}

export function polygonFromEffectiveGeometry(
  geometry: NonNullable<NonNullable<EffectiveMeasurementDefinition['measurement']>['effective_geometry']> | null | undefined,
): number[][] | null {
  if (!geometry || !isProvenDeployedGeometry(geometry)) return null;
  return zoneOverlayPolygon({
    polygon_norm: geometry.polygon_norm || undefined,
    bbox_norm: geometry.bbox_norm || undefined,
  });
}

/**
 * Camera-level deployed overlays from canonical EMD payloads only.
 * Draft, dormant, or unproven geometry is dropped. Polygons stay independent.
 */
export function composeOverviewDeployedCameraOverlays(args: {
  payloads: Array<EffectiveMeasurementDefinition | null | undefined>;
  cameraId?: string | null;
  deviceId?: string | null;
}): OverviewAreaGeometry[] {
  const expectedCamera = text(args.cameraId);
  const expectedDevice = text(args.deviceId);
  const overlays: OverviewAreaGeometry[] = [];
  const seen = new Set<string>();

  for (const payload of args.payloads) {
    if (!payload || payload.ok === false) continue;
    const scope = payload.operational_scope || {};
    const payloadCamera = text(scope.camera_id);
    const payloadDevice = text(scope.device_id);
    if (expectedCamera && payloadCamera && payloadCamera !== expectedCamera) continue;
    if (expectedDevice && payloadDevice && payloadDevice !== expectedDevice) continue;

    const measurement = payload.measurement || {};
    const polygon = polygonFromEffectiveGeometry(measurement.effective_geometry);
    if (!polygon || polygon.length < 3) continue;

    const zoneId = overviewV2PublicZoneId(measurement.scope_zone_id);
    const label = text(measurement.scope_name) || zoneId || 'Configured zone';
    const key = `${zoneId || label}|${JSON.stringify(polygon)}`;
    if (seen.has(key)) continue;
    seen.add(key);

    overlays.push({
      zoneId,
      label,
      polygon,
      sourceLabel: 'effective measurement definition',
      deployed: true,
      conditionId: text(payload.condition?.condition_id) || null,
      measurementScopeKind: text(measurement.scope_type) === 'ZONE_POLYGON' ? 'ZONE_POLYGON' : 'ENTIRE_CAMERA_VIEW',
    });
  }

  return overlays;
}

export function overlayFromProvenPolygon(args: {
  zoneId?: string | null;
  label?: string | null;
  polygon: number[][] | null | undefined;
  sourceLabel?: string | null;
  conditionId?: string | null;
  measurementScopeKind?: OverviewAreaGeometry['measurementScopeKind'];
}): OverviewAreaGeometry | null {
  if (!Array.isArray(args.polygon) || args.polygon.length < 3) return null;
  return {
    zoneId: overviewV2PublicZoneId(args.zoneId),
    label: text(args.label) || overviewV2PublicZoneId(args.zoneId) || 'Configured zone',
    polygon: args.polygon,
    sourceLabel: args.sourceLabel || 'effective measurement definition',
    deployed: true,
    conditionId: text(args.conditionId) || null,
    measurementScopeKind: args.measurementScopeKind || 'ZONE_POLYGON',
  };
}
